'use client';

import { TechnologyOption } from '@/lib/tech-stack-types';
import { prepareScatterData, getCategoryDisplayName } from '@/lib/visualization-data';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ZAxis } from 'recharts';
import { useMemo, useState } from 'react';

// Deterministic hash-based jitter to avoid Math.random() during render
function seededJitter(name: string, index: number): number {
  let hash = 0;
  const str = `${name}-${index}`;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return ((hash % 600) / 100); // Returns -3 to +3 range
}

interface TechStackScatterPlotProps {
  technologies: TechnologyOption[];
}

export default function TechStackScatterPlot({ technologies }: TechStackScatterPlotProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTech, setSelectedTech] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'name' | 'maturity' | 'communitySize' | 'popularityTrend'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const scatterData = useMemo(() => prepareScatterData(technologies), [technologies]);
  const categories = useMemo(() => [...new Set(scatterData.map(d => d.category))], [scatterData]);

  // Add deterministic jitter to prevent overlapping points (within ±3 units)
  const jitteredData = useMemo(() => scatterData.map((d, i) => ({
    ...d,
    maturity: d.maturity + seededJitter(d.name, i),
    communitySize: d.communitySize + seededJitter(d.name, i + 1000),
  })), [scatterData]);

  // Filter data by selected category
  const filteredData = selectedCategory
    ? jitteredData.filter(d => d.category === selectedCategory)
    : jitteredData;

  // Sort table data
  const sortedTableData = [...filteredData].sort((a, b) => {
    const aVal = sortField === 'popularityTrend' ? a[sortField] :
                 sortField === 'name' ? a[sortField].toLowerCase() : a[sortField];
    const bVal = sortField === 'popularityTrend' ? b[sortField] :
                 sortField === 'name' ? b[sortField].toLowerCase() : b[sortField];

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get unique colors for legend
  const categoryColors = new Map<string, string>();
  scatterData.forEach(d => categoryColors.set(d.category, d.color));

  return (
    <div className="space-y-6">
      {/* Title and Description */}
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Maturity vs Community Size</h3>
        <p className="text-foreground-secondary text-sm">
          Explore how technologies compare across maturity and community size.
          Bubble size indicates popularity trend strength.
        </p>
      </div>

      {/* Quadrant Explanation */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-surface rounded-lg p-4">
          <div className="text-sm font-semibold text-foreground mb-1">Top Right</div>
          <div className="text-xs text-foreground-secondary">
            Mature + Large Community = <span className="text-green-500 font-semibold">Safe Bets</span>
          </div>
        </div>
        <div className="bg-surface rounded-lg p-4">
          <div className="text-sm font-semibold text-foreground mb-1">Top Left</div>
          <div className="text-xs text-foreground-secondary">
            Early + Large Community = <span className="text-blue-500 font-semibold">Rising Stars</span>
          </div>
        </div>
        <div className="bg-surface rounded-lg p-4">
          <div className="text-sm font-semibold text-foreground mb-1">Bottom Right</div>
          <div className="text-xs text-foreground-secondary">
            Mature + Small Community = <span className="text-yellow-500 font-semibold">Niche Solutions</span>
          </div>
        </div>
        <div className="bg-surface rounded-lg p-4">
          <div className="text-sm font-semibold text-foreground mb-1">Bottom Left</div>
          <div className="text-xs text-foreground-secondary">
            Early + Small Community = <span className="text-accent font-semibold">Experimental</span>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div>
        <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
          Filter by Category
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 rounded text-sm transition-colors ${
              selectedCategory === null
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface text-foreground-secondary hover:bg-surface/80'
            }`}
          >
            All Categories
          </button>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                selectedCategory === category
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-surface text-foreground-secondary hover:bg-surface/80'
              }`}
            >
              {getCategoryDisplayName(category)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Scatter Plot */}
        <div className="lg:col-span-2 bg-surface rounded-lg p-6">
          <ResponsiveContainer width="100%" height={500}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
              <CartesianGrid stroke="#E8D5B7" strokeOpacity={0.1} />

              {/* Quadrant dividers */}
              <line x1="50%" y1="0%" x2="50%" y2="100%" stroke="#E8D5B7" strokeOpacity={0.2} strokeDasharray="5 5" />
              <line x1="0%" y1="50%" x2="100%" y2="50%" stroke="#E8D5B7" strokeOpacity={0.2} strokeDasharray="5 5" />

              <XAxis
                type="number"
                dataKey="maturity"
                name="Maturity"
                domain={[0, 100]}
                tick={{ fill: '#E8D5B7', fontSize: 11 }}
                label={{
                  value: 'Maturity Level →',
                  position: 'bottom',
                  fill: '#E8D5B7',
                  fontSize: 12,
                  offset: 40,
                }}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(value) => {
                  const labels: Record<number, string> = {
                    0: 'Experimental',
                    25: 'Early',
                    50: 'Stable',
                    75: 'Mature',
                    100: '',
                  };
                  return labels[value] || '';
                }}
              />
              <YAxis
                type="number"
                dataKey="communitySize"
                name="Community Size"
                domain={[0, 100]}
                tick={{ fill: '#E8D5B7', fontSize: 11 }}
                label={{
                  value: '← Community Size',
                  angle: -90,
                  position: 'left',
                  fill: '#E8D5B7',
                  fontSize: 12,
                  offset: 40,
                }}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(value) => {
                  const labels: Record<number, string> = {
                    0: '',
                    25: 'Small',
                    50: 'Medium',
                    75: 'Large',
                    100: 'Very Large',
                  };
                  return labels[value] || '';
                }}
              />
              <ZAxis
                type="number"
                dataKey="popularityTrend"
                range={[50, 400]}
                name="Popularity Trend"
              />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{
                  backgroundColor: '#0A1E2E',
                  border: '1px solid #E8D5B7',
                  borderRadius: '8px',
                  color: '#E8D5B7',
                }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-background border border-border rounded-lg p-3">
                        <p className="font-semibold text-foreground mb-2">{data.name}</p>
                        <p className="text-xs text-foreground-secondary">
                          Category: <span className="text-foreground">{getCategoryDisplayName(data.category)}</span>
                        </p>
                        <p className="text-xs text-foreground-secondary">
                          Maturity: <span className="text-foreground">{data.maturity}/100</span>
                        </p>
                        <p className="text-xs text-foreground-secondary">
                          Community: <span className="text-foreground">{data.communitySize}/100</span>
                        </p>
                        <p className="text-xs text-foreground-secondary">
                          Trend: <span className="text-accent">
                            {data.popularityTrend === 2.0 ? 'Rapidly Growing' :
                             data.popularityTrend === 1.5 ? 'Growing' :
                             data.popularityTrend === 1.0 ? 'Stable' : 'Declining'}
                          </span>
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Scatter
                data={filteredData}
                onClick={(data) => setSelectedTech(data.id)}
              >
                {filteredData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    fillOpacity={selectedTech === entry.id ? 1 : 0.7}
                    stroke={selectedTech === entry.id ? '#E8D5B7' : 'none'}
                    strokeWidth={2}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Legend and Details */}
        <div className="space-y-6">
          {/* Category Legend */}
          <div className="bg-surface rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Categories</h4>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
              {Array.from(categoryColors.entries()).map(([category, color]) => {
                const count = scatterData.filter(d => d.category === category).length;
                const isVisible = !selectedCategory || selectedCategory === category;

                return (
                  <div
                    key={category}
                    className={`flex items-center gap-2 ${isVisible ? 'opacity-100' : 'opacity-40'}`}
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-foreground truncate">
                        {getCategoryDisplayName(category)}
                      </div>
                    </div>
                    <div className="text-xs text-foreground-tertiary flex-shrink-0">
                      {count}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bubble Size Legend */}
          <div className="bg-surface rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Bubble Size</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-accent rounded-full opacity-70" />
                <span className="text-xs text-foreground-secondary">Declining</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-accent rounded-full opacity-70" />
                <span className="text-xs text-foreground-secondary">Stable</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-accent rounded-full opacity-70" />
                <span className="text-xs text-foreground-secondary">Growing</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent rounded-full opacity-70" />
                <span className="text-xs text-foreground-secondary">Rapidly Growing</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-surface rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Statistics</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-foreground-secondary">Total Technologies:</span>
                <span className="text-foreground font-semibold">{filteredData.length}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-foreground-secondary">Mature (&gt;75):</span>
                <span className="text-foreground font-semibold">
                  {filteredData.filter(d => d.maturity > 75).length}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-foreground-secondary">Large Community (&gt;75):</span>
                <span className="text-foreground font-semibold">
                  {filteredData.filter(d => d.communitySize > 75).length}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-foreground-secondary">Rapidly Growing:</span>
                <span className="text-foreground font-semibold">
                  {filteredData.filter(d => d.popularityTrend === 2.0).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Technology Details */}
      {selectedTech && (() => {
        const tech = scatterData.find(d => d.id === selectedTech);
        if (!tech) return null;

        return (
          <div className="bg-surface rounded-lg p-6 border-2 border-accent">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-foreground">{tech.name}</h4>
                <p className="text-sm text-foreground-secondary">{getCategoryDisplayName(tech.category)}</p>
              </div>
              <button
                onClick={() => setSelectedTech(null)}
                className="text-foreground-tertiary hover:text-foreground text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs font-semibold text-foreground-secondary mb-1">Maturity</div>
                <div className="text-2xl font-bold text-accent">{tech.maturity}</div>
                <div className="text-xs text-foreground-tertiary">out of 100</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground-secondary mb-1">Community</div>
                <div className="text-2xl font-bold text-accent">{tech.communitySize}</div>
                <div className="text-xs text-foreground-tertiary">out of 100</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground-secondary mb-1">Popularity</div>
                <div className="text-lg font-bold text-accent">
                  {tech.popularityTrend === 2.0 ? 'Rapid ↗' :
                   tech.popularityTrend === 1.5 ? 'Growing ↗' :
                   tech.popularityTrend === 1.0 ? 'Stable →' : 'Declining ↘'}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground-secondary mb-1">Position</div>
                <div className="text-sm font-semibold text-foreground">
                  {tech.maturity > 60 && tech.communitySize > 60 ? 'Safe Bet' :
                   tech.maturity < 40 && tech.communitySize > 60 ? 'Rising Star' :
                   tech.maturity > 60 && tech.communitySize < 40 ? 'Niche' : 'Experimental'}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Data Table */}
      <div className="bg-surface rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border-muted">
          <h4 className="text-lg font-semibold text-foreground">All Technologies</h4>
          <p className="text-sm text-foreground-secondary">
            {filteredData.length} {selectedCategory ? getCategoryDisplayName(selectedCategory) : 'total'} technologies
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background border-b border-border-muted">
                <th className="text-left p-3 text-foreground-secondary font-semibold">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 hover:text-foreground transition-colors"
                  >
                    Technology
                    {sortField === 'name' && (
                      <span className="text-accent">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left p-3 text-foreground-secondary font-semibold">Category</th>
                <th className="text-right p-3 text-foreground-secondary font-semibold">
                  <button
                    onClick={() => handleSort('maturity')}
                    className="flex items-center gap-2 hover:text-foreground transition-colors ml-auto"
                  >
                    Maturity
                    {sortField === 'maturity' && (
                      <span className="text-accent">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-right p-3 text-foreground-secondary font-semibold">
                  <button
                    onClick={() => handleSort('communitySize')}
                    className="flex items-center gap-2 hover:text-foreground transition-colors ml-auto"
                  >
                    Community
                    {sortField === 'communitySize' && (
                      <span className="text-accent">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left p-3 text-foreground-secondary font-semibold">
                  <button
                    onClick={() => handleSort('popularityTrend')}
                    className="flex items-center gap-2 hover:text-foreground transition-colors"
                  >
                    Trend
                    {sortField === 'popularityTrend' && (
                      <span className="text-accent">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left p-3 text-foreground-secondary font-semibold">Position</th>
              </tr>
            </thead>
            <tbody>
              {sortedTableData.map((tech, index) => {
                const position =
                  tech.maturity > 60 && tech.communitySize > 60 ? { label: 'Safe Bet', color: 'text-green-500' } :
                  tech.maturity < 40 && tech.communitySize > 60 ? { label: 'Rising Star', color: 'text-blue-500' } :
                  tech.maturity > 60 && tech.communitySize < 40 ? { label: 'Niche', color: 'text-yellow-500' } :
                  { label: 'Experimental', color: 'text-accent' };

                const trendLabel =
                  tech.popularityTrend === 2.0 ? { label: 'Rapidly Growing', icon: '↗↗' } :
                  tech.popularityTrend === 1.5 ? { label: 'Growing', icon: '↗' } :
                  tech.popularityTrend === 1.0 ? { label: 'Stable', icon: '→' } :
                  { label: 'Declining', icon: '↘' };

                return (
                  <tr
                    key={tech.id}
                    className={`border-b border-border-muted hover:bg-background/50 transition-colors cursor-pointer ${
                      selectedTech === tech.id ? 'bg-accent-muted' : ''
                    }`}
                    onClick={() => setSelectedTech(tech.id)}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tech.color }}
                        />
                        <span className="text-foreground font-medium">{tech.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-foreground-secondary text-xs">
                      {getCategoryDisplayName(tech.category)}
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-foreground font-mono">{Math.round(tech.maturity)}</span>
                      <span className="text-foreground-tertiary text-xs">/100</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-foreground font-mono">{Math.round(tech.communitySize)}</span>
                      <span className="text-foreground-tertiary text-xs">/100</span>
                    </td>
                    <td className="p-3">
                      <span className="text-foreground-secondary text-xs">
                        {trendLabel.label} {trendLabel.icon}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs font-semibold ${position.color}`}>
                        {position.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredData.length === 0 && (
          <div className="p-8 text-center text-foreground-tertiary">
            No technologies found in this category.
          </div>
        )}
      </div>
    </div>
  );
}
